// frontend/src/components/ActivityCard.js
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { HandPalm, LockSimple } from "phosphor-react";
import { api } from "api";
import CommentsSection from "./CommentsSection";
import EngagementModal from "./EngagementModal";
import ActivityFormFields from "./ActivityFormFields";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

// Mentions: support @"Full Name" (quoted, up to 60 chars) or unquoted up to TWO words.
const TOKEN_RE =
  /(^|[\s])@("([^"\n]{1,60})"|([A-Za-z0-9_.]+(?: [A-Za-z0-9_.]+)?))(?=$|\s|[.,!?;:)\]])/g;


const toAbsolute = (url) => {
  if (!url) return "";

  if (/^(https?:|blob:|data:|file:|capacitor:)/i.test(url)) {
    return url;
  }

  // Frontend files inside public/images
  if (url.startsWith("/images/") || url.startsWith("images/")) {
    return url.startsWith("/") ? url : `/${url}`;
  }

  // Backend-uploaded media
  const base = api.defaults?.baseURL?.replace(/\/+$/, "") || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};

const toAvatar = (u) =>
  toAbsolute(
    u?.profile_pic_url ||
    u?.avatar_url ||
    u?.profile_image_url ||
    u?.image_url ||
    null
  ) || DEFAULT_PROFILE_IMAGE;

const isVideo = (u) =>
  /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(u || "");

const isVideoMime = (t) => typeof t === "string" && t.toLowerCase().startsWith("video/");

// returns an array of { url, type } from activity
const getMediaList = (a) => {
  const galleryUrls =
    Array.isArray(a?.media_urls)
      ? a.media_urls.filter(Boolean)
      : [];

  const urls =
    galleryUrls.length > 0
      ? galleryUrls
      : a?.media_url
        ? [a.media_url]
        : [];

  const types = Array.isArray(a?._media_types)
    ? a._media_types
    : a?._media_type
      ? [a._media_type]
      : [];

  return urls.map((url, index) => ({
    url,
    type:
      types[index] ||
      a?.media_mime ||
      a?.media_type ||
      null,
  }));
};
 

const getMediaUrls = (activity) => {
  const galleryUrls = Array.isArray(activity?.media_urls)
    ? activity.media_urls.filter(Boolean)
    : [];

  if (galleryUrls.length > 0) {
    return galleryUrls;
  }

  return activity?.media_url
    ? [activity.media_url]
    : [];
};

// iOS detection (for Apple Maps links)
const isiOS = () =>
  typeof navigator !== "undefined" &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

const formatTimestamp = (input) => {
  if (!input) return "";

  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "";

  const now = new Date();

  const startOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const today = startOfDay(now);
  const targetDay = startOfDay(d);

  const diffDays = Math.round((today - targetDay) / 86400000);

  const time = d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(" ", "");

  if (diffDays === 0) return `Today ${time}`;
  if (diffDays === 1) return `Yesterday ${time}`;

  const date = d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return `${date} ${time}`;
};

// For <input type="datetime-local">
const toLocalDatetimeValue = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const ActivityCard = ({
  activity: activityProp,
  currentUser,
  onTagClick,
  onUpdated,
  readOnly = false,
  className = "",
  contentClassName = "p-6",
}) => {
  // keep local copy so we can reflect saved edits immediately
  const [activity, setActivity] = useState(activityProp);
  useEffect(() => setActivity(activityProp), [activityProp]);

  const nav = useNavigate();

  // engagement
  const [hfPending, setHfPending] = useState(false);
  const [hasHf, setHasHf] = useState(!!activityProp.has_high_fived);
  const [hfCount, setHfCount] = useState(activityProp.high_five_count || 0);
  const [commentCount, setCommentCount] = useState(activityProp.comment_count || 0);
  const [previewUsers, setPreviewUsers] = useState(activityProp.highfivers_preview || []);
  const [commentersPreview, setCommentersPreview] = useState(activityProp.commenters_preview || []);
  const [showModal, setShowModal] = useState(false);

  // edit mode
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    tags: "",
    mood: "",
    duration: "",
    location: "",
    timestamp: "",
    media_file: null,
    media_files: [],
    existing_media_urls: [],
    circle_ids: [],
    place_id: undefined,
    lat: undefined,
    lng: undefined,
    visibility: "",
    base_visibility: "",
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  // circles (edit chooser list)
  const [joinedCircles, setJoinedCircles] = useState([]);

  // circles (view mode resolved names)
  const [viewCircles, setViewCircles] = useState([]); // [{id, name}]
  const [circlesLoading, setCirclesLoading] = useState(false);

  // delete workflow
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const ownerId =
    activity.user?.id ??
    activity.user_id ??
    activity.author_id ??
    null;

  const ownerUser =
    activity.user ||
    activity.author ||
    activity.owner ||
    (currentUser && currentUser.id === ownerId ? currentUser : null);

  const displayName =
    ownerUser?.name ||
    ownerUser?.display_name ||
    ownerUser?.username ||
    activity.user_name ||
    activity.author_name ||
    "User";

  const authorId = ownerUser?.id || ownerId;

  const ownerAvatar =
    ownerUser?.profile_pic_url ||
    activity.user?.profile_pic_url ||
    activity.author?.profile_pic_url ||
    currentUser?.profile_pic_url ||
    DEFAULT_PROFILE_IMAGE;

  // engagement summary visibility + label
  const hasEngagement = hfCount > 0 || commentCount > 0;
  const engagementText = (() => {
    const parts = [];
    if (hfCount > 0) parts.push(`${hfCount} ${hfCount === 1 ? "high five" : "high fives"}`);
    /* if (commentCount > 0) parts.push(`${commentCount} ${commentCount === 1 ? "comment" : "comments"}`); */
    return parts.join(" · ");
  })();

  // ---------- user resolver (label/handle -> id) ----------
  const [idLookup, setIdLookup] = useState({}); // { "sharrieh": 123, "Sharrie H": 123 }
  useEffect(() => {
    if (readOnly) return; // ⛔ skip in preview
    let cancelled = false;
    const run = async () => {
      try {
        const token = localStorage.getItem("token");
        const meId = localStorage.getItem("user_id");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const url = meId ? `/connections/following/${meId}` : "/connections/following/me";
        const { data } = await api.get(url, { headers });
        const list = Array.isArray(data) ? data : [];
        const map = {};
        for (const u of list) {
          const id = u.id;
          const label =
            u.name || u.display_name || u.username || (id ? `User #${id}` : "User");
          const handle = u.username || "";
          if (label) map[String(label).toLowerCase()] = id;
          if (handle) map[String(handle).toLowerCase()] = id;
        }
        if (!cancelled) setIdLookup(map);
      } catch {
        // fail quietly; fallback still works
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [readOnly]);

  // numeric fallback so "@123" resolves to id 123
  const resolveId = (labelOrHandle) => {
    const key = String(labelOrHandle || "").toLowerCase();
    if (/^\d+$/.test(key)) return Number(key);
    return idLookup[key];
  };

  // hydrate engagement
  useEffect(() => {
    if (readOnly) return; // ⛔ skip in preview
    const token = localStorage.getItem("token");
    if (!token) return;
    let mounted = true;
    api
      .get(`/activities/${activityProp.id}/engagement`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (!mounted) return;
        setHasHf(!!r.data?.has_high_fived);
        setHfCount(Number(r.data?.high_five_count || 0));
        setCommentCount(Number(r.data?.comment_count || 0));
        setPreviewUsers(r.data?.highfivers_preview || []);
        setCommentersPreview(r.data?.commenters_preview || []);

        // hydrate id lookup with users we already have (owner + previews)
        try {
          const extra = {};
          const addUser = (u) => {
            if (!u?.id) return;
            if (u.username) extra[String(u.username).toLowerCase()] = u.id;
            if (u.name) extra[String(u.name).toLowerCase()] = u.id;
            if (u.display_name) extra[String(u.display_name).toLowerCase()] = u.id;
          };
          const owner = activity.user || (
            (currentUser && currentUser.id === (activity.user_id ?? activity.author_id))
              ? currentUser
              : null
          );
          addUser(owner);
          (r.data?.highfivers_preview || []).forEach(addUser);
          (r.data?.commenters_preview || []).forEach(addUser);
          if (Object.keys(extra).length) {
            setIdLookup((m) => ({ ...m, ...extra }));
          }
        } catch {
          /* ignore */
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [activityProp.id, activity.user, activity.user_id, activity.author_id, currentUser, readOnly]);

  // In ActivityCard.js (near your other effects)
  useEffect(() => {
    if (readOnly) return; // ⛔ skip in preview
    if (!editing) return;
    if (Array.isArray(form.circle_ids) && form.circle_ids.length) return;

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const { data } = await api.get(`/activities/${activity.id}/circles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ids = Array.isArray(data)
          ? data.map((g) => Number(g.id)).filter(Number.isFinite)
          : [];
        if (!cancelled && ids.length) {
          setForm((f) => ({ ...f, circle_ids: ids }));
        }
      } catch {/* ignore */}
    })();

    return () => { cancelled = true; };
  }, [editing, activity.id, readOnly]);  // also depends on `form` setter in scope

  // live-update commenters preview when a new comment is posted on this activity
  useEffect(() => {
    if (readOnly) return; // ⛔ skip in preview
    const onAdded = (e) => {
      const id = e?.detail?.activityId;
      if (!id || id !== activity.id) return;
      if (!currentUser?.id) return;
      setCommentersPreview((prev) => {
        if (prev?.some((u) => u?.id === currentUser.id)) return prev;
        const me = {
          id: currentUser.id,
          profile_pic_url: currentUser.profile_pic_url,
          name: currentUser.name || currentUser.username || "User",
        };
        return [me, ...(Array.isArray(prev) ? prev : [])].slice(0, 8);
      });
    };
    window.addEventListener("comment-added", onAdded);
    return () => window.removeEventListener("comment-added", onAdded);
  }, [activity.id, currentUser, readOnly]);

  useEffect(() => {
    if (readOnly) return; // ⛔ skip in preview
    const onDeleted = (e) => {
      const id = e?.detail?.activityId;
      const userId = e?.detail?.userId;
      if (!id || id !== activity.id) return;

      if (userId) {
        setCommentersPreview((prev) => (Array.isArray(prev) ? prev.filter((u) => u?.id !== userId) : []));
      }
      setCommentCount((n) => Math.max(0, n - 1));

      const token = localStorage.getItem("token");
      if (!token) return;
      api
        .get(`/activities/${activity.id}/engagement`, {
          headers: { Authorization: { Authorization: `Bearer ${token}` }?.Authorization || `Bearer ${token}` },
        })
        .then((r) => {
          setHasHf(!!r.data?.has_high_fived);
          setHfCount(Number(r.data?.high_five_count || 0));
          setCommentCount(Number(r.data?.comment_count || 0));
          setPreviewUsers(r.data?.highfivers_preview || []);
          setCommentersPreview(r.data?.commenters_preview || []);
        })
        .catch(() => {});
    };

    window.addEventListener("comment-deleted", onDeleted);
    return () => window.removeEventListener("comment-deleted", onDeleted);
  }, [activity.id, readOnly]);

  // Load joined circles for the current user (for edit mode chooser)
  useEffect(() => {
    if (readOnly) return; // ⛔ skip in preview
    const token = localStorage.getItem("token");
    if (!token || !currentUser?.id) return;
    const headers = { Authorization: `Bearer ${token}` };
    api
      .get(`/users/${currentUser.id}/groups`, { headers })
      .then((res) => setJoinedCircles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setJoinedCircles([]));
  }, [currentUser?.id, readOnly]);

  // --- helper: pull circles straight from activity if present ---
  const extractCirclesFromActivity = (a) => {
    const lists = [a?.circles, a?.groups, a?._circles];
    const out = [];
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const g of list) {
        const id = g?.id ?? g?.group_id ?? g?.circle_id;
        const name = g?.name ?? g?.title ?? g?.handle;
        if (id && name) out.push({ id, name });
      }
    }
    // de-dupe by id
    const seen = new Set();
    return out.filter(c => (seen.has(String(c.id)) ? false : seen.add(String(c.id))));
  };

  // Resolve circles for view mode (names under Location)
  useEffect(() => {
    let cancelled = false;

    const loadPreviewCircles = () => {
      const embedded = extractCirclesFromActivity(activity);
      setViewCircles(embedded);
      setCirclesLoading(false);
    };

    const load = async () => {
      setCirclesLoading(true);

      // In readOnly preview: never hit network; just use embedded data / ids
      if (readOnly) {
        loadPreviewCircles();
        return;
      }

      try {
        // 1) If activity already embeds circles/groups, use them immediately.
        const embedded = extractCirclesFromActivity(activity);
        if (embedded.length) {
          if (!cancelled) setViewCircles(embedded);
          return;
        }

        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 2) Collect IDs (accept strings or numbers)
        const idSet = new Set();

        for (const arr of [activity?.circle_ids, activity?.group_ids]) {
          if (Array.isArray(arr)) arr.forEach(id => id != null && idSet.add(String(id)));
        }
        if (activity?.circle_id != null) idSet.add(String(activity.circle_id));

        const ids = Array.from(idSet);

        if (!ids.length) {
          try {
            const r = await api.get(`/activities/${activity.id}/circles`, { headers });
            const list = Array.isArray(r?.data) ? r.data : (r?.data?.circles || r?.data?.groups || []);
            const circles = (list || [])
              .map(g => {
                const id = g?.id ?? g?.group_id ?? g?.circle_id;
                const name = g?.name ?? g?.title ?? g?.handle ?? (id ? `Circle #${id}` : "");
                return id ? { id, name } : null;
              })
              .filter(Boolean);
            if (circles.length) {
              if (!cancelled) setViewCircles(circles);
              return;
            }
          } catch (_) {
            // ignore; we'll try per-id below if we later discover ids
          }
        }

        if (ids.length) {
          let circles = [];
          try {
            const qs = ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
            const bulk = await api.get(`/groups/bulk?${qs}`, { headers });
            const list = Array.isArray(bulk?.data) ? bulk.data : (bulk?.data?.groups || []);
            circles = list
              .map(g => {
                const id = g?.id ?? g?.group_id ?? g?.circle_id;
                const name = g?.name ?? g?.title ?? g?.handle ?? (id ? `Circle #${id}` : "");
                return id ? { id, name } : null;
              })
              .filter(Boolean);
          } catch {
            // fall through to per-id
          }

          if (!circles.length) {
            const results = await Promise.all(
              ids.map(async (id) => {
                try {
                  const r = await api.get(`/groups/${id}`, { headers });
                  const g = r?.data || {};
                  const name = g?.name ?? g?.title ?? g?.handle ?? `Circle #${id}`;
                  return { id, name };
                } catch {
                  return { id, name: `Circle #${id}` };
                }
              })
            );
            circles = results.filter(Boolean);
          }

          if (!cancelled) setViewCircles(circles);
        } else {
          if (!cancelled) setViewCircles([]);
        }
      } finally {
        if (!cancelled) setCirclesLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  // stringify arrays so changes trigger; avoid object deps that cause loops
  }, [activity.id, String(activity.circle_id ?? ""), JSON.stringify(activity.circle_ids ?? []), JSON.stringify(activity.groups ?? activity.circles ?? []), readOnly]);

  // Combine high-fivers and commenters into one unique list (by id), show up to 4
  const avatarUsers = useMemo(() => {
    const seen = new Set();
    const combined = [];
    const pushUnique = (u) => {
      if (!u || !u.id || seen.has(u.id)) return;
      seen.add(u.id);
      combined.push(u);
    };
    (previewUsers || []).forEach(pushUnique);
    (commentersPreview || []).forEach(pushUnique);
    return combined.slice(0, 4);
  }, [previewUsers, commentersPreview]);

  const toggleHighFive = async () => {
    if (readOnly) return; // ⛔ no-op in preview
    if (hfPending) return;
    const token = localStorage.getItem("token");
    if (!token) return alert("Please log in to high five.");
    setHfPending(true);

    // keep snapshots for optimistic rollback
    const prevHasHf = hasHf;
    const prevHfCount = hfCount;
    const prevPreview = previewUsers;

    try {
      if (hasHf) {
        // optimistically update
        setHasHf(false);
        setHfCount((n) => Math.max(0, n - 1));
        if (currentUser?.id) {
          setPreviewUsers((prev) => (Array.isArray(prev) ? prev.filter((u) => u?.id !== currentUser.id) : []));
        }

        await api.delete("/activities/highfive", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          data: { activity_id: activity.id, sender_id: currentUser.id },
        });
      } else {
        // optimistically update
        setHasHf(true);
        setHfCount((n) => n + 1);
        if (currentUser?.id) {
          setPreviewUsers((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            if (list.some((u) => u?.id === currentUser.id)) return list;
            const me = {
              id: currentUser.id,
              profile_pic_url: currentUser.profile_pic_url,
              name: currentUser.name || currentUser.username || "User",
            };
            return [me, ...list].slice(0, 8);
          });
        }

        await api.post(
          "/activities/highfive",
          { activity_id: activity.id, sender_id: currentUser.id },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" } }
        );
      }
    } catch (e) {
      // rollback on failure
      setHasHf(prevHasHf);
      setHfCount(prevHfCount);
      setPreviewUsers(prevPreview);

      const detail = e?.response?.data?.detail || e.message;
      if (!prevHasHf && typeof detail === "string" && detail.toLowerCase().includes("already")) {
        setHasHf(true);
      } else {
        alert(detail || "High five failed");
      }
    } finally {
      setHfPending(false);
    }
  };

  /** Begin inline edit — map activity -> ActivityFormFields shape */
  const beginEdit = () => {
    const existingUrls = getMediaUrls(activity);

    setForm({
      title: activity.title || "",
      description: activity.description || "",
      tags: Array.isArray(activity.tags)
        ? activity.tags.join(", ")
        : activity.tags || "",
      mood: activity.mood || "",
      duration: activity.duration || "",
      location: activity.location || "",
      timestamp: toLocalDatetimeValue(activity.timestamp),
      media_file: null,
      media_files: [],
      existing_media_urls: existingUrls,
      circle_ids: Array.isArray(activity.circle_ids)
        ? activity.circle_ids.map(Number).filter(Number.isFinite)
        : [],
      place_id: activity.place_id,
      lat: activity.lat,
      lng: activity.lng,
      visibility:
        activity.visibility === "private"
          ? "private"
          : activity.visibility || "",
      base_visibility: activity.visibility || "",
    });

    setPreviewUrl(null);
    setError("");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  // helpers to try multiple endpoints/methods (unchanged)...

  const tryJsonUpdate = async (payload, headers, id) => {
    const paths = [`/activities/${id}`, `/activity/${id}`, `/activities/update/${id}`];
    const methods = ["patch", "put"];
    for (const p of paths) {
      for (const m of methods) {
        try {
          const { data } = await api[m](p, payload, {
            headers: { ...headers, "Content-Type": "application/json" },
          });
          return data ?? payload;
        } catch (_) {}
      }
    }
    throw new Error("No JSON update endpoint accepted the payload.");
  };

  const tryMultipartUpdate = async (fd, headers, id) => {
    const basePaths = [
      `/activities/${id}`,
      `/activity/${id}`,
      `/activities/${id}/upload`,
      `/activities/${id}/media`,
      `/activities/${id}/file`,
      `/activities/update/${id}`,
    ];
    const methods = ["patch", "put", "post"];
    for (const p of basePaths) {
      for (const m of methods) {
        try {
          const { data } = await api[m](p, fd, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          });
          return data ?? null;
        } catch (_) {}
      }
    }
    throw new Error("No update endpoint accepted file upload.");
  };

  const normalizeTags = (t) => {
    if (Array.isArray(t)) return t;
    if (typeof t === "string") {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
      return t.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const saveEdit = async () => {
    if (readOnly) return; // no-op in preview

    setSaving(true);
    setError("");

    const token = localStorage.getItem("token");

    if (!token) {
      setError("Not authenticated");
      setSaving(false);
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const tagList = form.tags
      ? form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    const tsISO = form.timestamp
      ? new Date(form.timestamp).toISOString()
      : "";

    /*
    * Existing media that the user has not removed.
    *
    * Important:
    * These should be the original backend URLs stored in
    * form.existing_media_urls—not the toAbsolute() display versions.
    */
    const retainedMediaUrls = Array.isArray(form.existing_media_urls)
      ? form.existing_media_urls.filter(Boolean)
      : [];

    /*
    * Support the new media_files array, while retaining media_file
    * as a temporary fallback for older form behavior.
    */
    const selectedMediaFiles =
      Array.isArray(form.media_files) && form.media_files.length > 0
        ? form.media_files
        : form.media_file
          ? [form.media_file]
          : [];

    /*
    * Existing retained media plus newly selected media cannot exceed 5.
    */
    const availableMediaSlots = Math.max(
      0,
      5 - retainedMediaUrls.length
    );

    const newMediaFiles = selectedMediaFiles.slice(
      0,
      availableMediaSlots
    );

    try {
      /*
      * Always use multipart for edit.
      *
      * This allows the request to communicate all three cases:
      * 1. Keep existing media.
      * 2. Remove some or all existing media.
      * 3. Add one or more new media files.
      */
      const fd = new FormData();

      fd.append("title", String(form.title ?? ""));
      fd.append("description", String(form.description ?? ""));
      fd.append("mood", String(form.mood ?? ""));
      fd.append("duration", String(form.duration ?? ""));
      fd.append("location", String(form.location ?? ""));
      fd.append("timestamp", tsISO || "");
      fd.append("tags", JSON.stringify(tagList));

      fd.append(
        "circle_ids",
        JSON.stringify(
          Array.isArray(form.circle_ids)
            ? form.circle_ids
            : []
        )
      );

      /*
      * Always send the retained media list.
      *
      * An empty array means the user removed all existing media.
      */
      fd.append(
        "existing_media_urls",
        JSON.stringify(retainedMediaUrls)
      );

      /*
      * Append every newly selected file under the same multipart key.
      * FastAPI receives these as List[UploadFile].
      */
      newMediaFiles.forEach((file) => {
        fd.append("media_files", file);
      });

      if (form.place_id) {
        fd.append("place_id", String(form.place_id));
      }

      if (typeof form.lat === "number") {
        fd.append("lat", String(form.lat));
      }

      if (typeof form.lng === "number") {
        fd.append("lng", String(form.lng));
      }

      if (form.visibility) {
        fd.append("visibility", String(form.visibility));
      }

      const updated = await tryMultipartUpdate(
        fd,
        headers,
        activity.id
      );

      const updatedTags = normalizeTags(
        updated?.tags ?? tagList
      );

      /*
      * Prefer the backend's final gallery.
      *
      * The backend should return:
      * media_urls: all final retained + uploaded media
      * media_url: the first media item, for legacy compatibility
      */
      const returnedMediaUrls = Array.isArray(updated?.media_urls)
        ? updated.media_urls.filter(Boolean)
        : [];

      const nextMediaUrls =
        returnedMediaUrls.length > 0
          ? returnedMediaUrls
          : updated?.media_url
            ? [updated.media_url]
            : retainedMediaUrls;

      const nextMediaUrl =
        typeof updated?.media_url !== "undefined"
          ? updated.media_url
          : nextMediaUrls[0] || null;

      const merged = {
        ...activity,
        ...updated,

        title:
          updated?.title ??
          form.title,

        description:
          updated?.description ??
          form.description,

        mood:
          updated?.mood ??
          form.mood,

        duration:
          updated?.duration ??
          form.duration,

        location:
          updated?.location ??
          form.location,

        timestamp:
          updated?.timestamp ??
          (tsISO || activity.timestamp),

        tags: updatedTags,

        media_url: nextMediaUrl,
        media_urls: nextMediaUrls,

        place_id:
          updated?.place_id ??
          form.place_id,

        lat:
          updated?.lat ??
          form.lat,

        lng:
          updated?.lng ??
          form.lng,

        circle_ids: Array.isArray(updated?.circle_ids)
          ? updated.circle_ids
          : Array.isArray(form.circle_ids)
            ? form.circle_ids
            : activity.circle_ids,

        circle_id:
          typeof updated?.circle_id !== "undefined"
            ? updated.circle_id
            : Array.isArray(form.circle_ids) &&
                form.circle_ids.length > 0
              ? form.circle_ids[0]
              : activity.circle_id,

        visibility:
          typeof updated?.visibility !== "undefined"
            ? updated.visibility
            : form.visibility || "",

        is_private:
          typeof updated?.is_private !== "undefined"
            ? updated.is_private
            : activity.is_private,
      };

      setActivity(merged);
      setEditing(false);

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(null);

      /*
      * Clear local file selections after a successful save.
      * The existing-media state now comes from the saved response.
      */
      setForm((current) => ({
        ...current,
        media_file: null,
        media_files: [],
        existing_media_urls: nextMediaUrls,
      }));

      onUpdated?.(merged);

      window?.dispatchEvent?.(
        new CustomEvent("activity-updated", {
          detail: merged,
        })
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;

      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail
                .map((item) => item?.msg || String(item))
                .join(" • ")
            : Array.isArray(e?.response?.data)
              ? e.response.data
                  .map((item) => item?.msg || String(item))
                  .join(" • ")
              : e?.message || "Failed to update";

      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  // delete handler (tries common endpoints)
  const deleteActivity = async () => {
    if (readOnly) return; // ⛔ no-op in preview
    setDeleting(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Not authenticated");
      const headers = { Authorization: `Bearer ${token}` };
      const paths = [`/activities/${activity.id}`, `/activity/${activity.id}`];

      let ok = false;
      for (const p of paths) {
        try {
          await api.delete(p, { headers });
          ok = true;
          break;
        } catch (_) {
          /* try next */
        }
      }
      if (!ok) throw new Error("Failed to delete activity");

      setIsDeleted(true);
      setConfirmingDelete(false);

      window?.dispatchEvent?.(
        new CustomEvent("activity:deleted", {
          detail: { id: activity.id, user_id: activity.user_id || currentUser?.id },
        })
      );
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Delete failed");
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  /** ---------------- VIEW MODE ---------------- */
  if (isDeleted) return null;

  if (!editing) {
    const hasCoords = typeof activity.lat === "number" && typeof activity.lng === "number";

    // iOS-friendly map deep link (Apple Maps if iOS, otherwise Google Maps)
    const locationHref = (() => {
      if (activity.location) {
        const q = encodeURIComponent(activity.location);
        return isiOS() ? `maps://?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
      }
      if (hasCoords) {
        const q = `${activity.lat},${activity.lng}`;
        return isiOS() ? `maps://?q=${encodeURIComponent(q)}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
      }
      return null;
    })();

    const MentionLink = ({ id, handle, children }) => {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    
      const go = (uid) => {
        if (readOnly) return; // disable in preview
        nav(`/profile/${uid}`);
      };
    
      const onClick = async (e) => {
        e.preventDefault();
        if (id) {
          go(id);
          return;
        }
        const q = String(handle || "").trim();
        if (!q) return;
        try {
          const r = await api.get(`/users/search`, { params: { q }, headers });
          const arr = Array.isArray(r.data) ? r.data : [];
          const exact =
            arr.find((u) => u?.username && String(u.username).toLowerCase() === q.toLowerCase()) ||
            arr.find((u) => String(u.name || "").toLowerCase() === q.toLowerCase());
          if (exact?.id) go(exact.id);
        } catch {
          /* silent no-op */
        }
      };
    
      const className = "font-semibold text-blue-700 hover:underline cursor-pointer";
    
      if (id) {
        return readOnly ? (
          <span className={className}>{children}</span>
        ) : (
          <Link to={`/profile/${id}`} className={className}>
            {children}
          </Link>
        );
      }
    
      return (
        <button
          type="button"
          onClick={onClick}
          className={className}
          style={{ background: "transparent", padding: 0, margin: 0 }}
        >
          {children}
        </button>
      );
    };

    const renderDescriptionNodes = (text) => {
      const s = String(text || "");
      const nodes = [];
      let last = 0;
    
      for (const m of s.matchAll(TOKEN_RE)) {
        const start = m.index ?? 0;
        const end = start + m[0].length;
    
        if (start > last) nodes.push(s.slice(last, start));
    
        const prefix = m[1] || "";
        if (prefix) nodes.push(prefix);
    
        // Group 3 = quoted name, Group 4 = unquoted (1–2 words)
        const raw = (m[3] || m[4] || "").trim();
        if (!raw) { nodes.push("@"); last = end; continue; }
    
        const key = raw.toLowerCase();
        const resolvedId = resolveId(key) || resolveId(raw);
    
        nodes.push(
          <MentionLink key={`m-${raw}-${start}`} id={resolvedId} handle={raw}>
            @{raw}
          </MentionLink>
        );
    
        last = end;
      }
      if (last < s.length) nodes.push(s.slice(last));
      return nodes;
    };

    const isOwner = !!currentUser?.id && !!ownerId && currentUser.id === ownerId;
    const isPrivate =   activity.visibility === "private" || (!activity.visibility && activity.is_private === true);

    console.log("Activity circles", { id: activity?.id, circles: activity?.circles, groups: activity?.groups, circle_ids: activity?.circle_ids, group_ids: activity?.group_ids });
    console.log("Resolved viewCircles", viewCircles);

    return (
      <div className={`bg-white rounded-2xl ${contentClassName} ${className}`}>
        <div className="flex items-start justify-between text-sm text-gray-500">
          <div className="flex items-start gap-2 min-w-0">
            {authorId ? (
              <Link
                to={readOnly ? "#" : `/profile/${authorId}`}
                onClick={readOnly ? (e) => e.preventDefault() : undefined}
                className="shrink-0 rounded-full hover:ring-2 hover:ring-blue-400"
                title="View profile"
              >
              <img
                src={ownerAvatar}
                alt={displayName || "user"}
                className="w-8 h-8 rounded-full object-cover"
                loading="lazy"
                decoding="async"
              />
              </Link>
            ) : (
              <img
                src={ownerAvatar}
                alt={displayName || "user"}
                className="w-8 h-8 rounded-full object-cover"
                loading="lazy"
                decoding="async"
              />
            )}

            <div className="flex flex-col min-w-0 leading-tight">
              {authorId ? (
                <Link
                  to={readOnly ? "#" : `/profile/${authorId}`}
                  onClick={readOnly ? (e) => e.preventDefault() : undefined}
                  className="text-gray-700 hover:text-blue-600 hover:font-bold focus:outline-none rounded truncate"
                  title="View profile"
                >
                  {displayName}
                </Link>
              ) : (
                <span className="text-gray-700 truncate">{displayName}</span>
              )}

              <p className="text-xs text-gray-500">
                {formatTimestamp(activity.timestamp || activity.created_at)}
              </p>
            </div>
          </div>

          {!readOnly && ownerUser?.id === currentUser?.id && (
            <button
              type="button"
              onClick={beginEdit}
              className="text-xs text-blue-600 hover:font-semibold !min-h-0 !min-w-0 py-0 shrink-0"
            >
              Edit Activity
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 mt-1">
          {isOwner && isPrivate && (
            <LockSimple
              size={16}
              weight="fill"
              className="text-gray-500 shrink-0"
              aria-label="Private activity"
              title="Private activity"
            />
          )}
          <h3 className="font-bold text-lg text-blue-600 break-words">
            {activity.title}
          </h3>
        </div>


        {activity.description && (
          <p className="text-sm mt-1 mb-1 whitespace-pre-wrap">
            {renderDescriptionNodes(activity.description)}
          </p>
        )}

        {/* Details */}
        <div className="sm:mt-2 text-sm text-gray-600 space-y-1">
          {activity.mood && (
            <p>
              <span className="font-semibold">Mood:</span> {activity.mood}
            </p>
          )}
          {activity.duration && (
            <p>
              <span className="font-semibold">Duration:</span> {activity.duration}
            </p>
          )}
          {/* Location */}
          {locationHref && (
            <p>
              <span className="font-semibold">Location:</span>{" "}
              <a
                href={readOnly ? "#" : locationHref}
                onClick={readOnly ? (e) => e.preventDefault() : undefined}
                target={readOnly ? undefined : "_blank"}
                rel={readOnly ? undefined : "noopener noreferrer"}
                className="text-blue-500 underline"
              >
                {activity.location || `${activity.lat}, ${activity.lng}`}
              </a>
            </p>
          )}

          {/* Circles (below Location) */}
          {!circlesLoading && viewCircles.length > 0 && (
            <div className="grid grid-cols-[auto_1fr] !mt-2 !mb-1 gap-x-3 gap-y-2 items-start">
              <span className="font-semibold">Circles:</span>

              <div className="flex flex-wrap gap-2">
                {viewCircles.map((c) =>
                  readOnly ? (
                    <span
                      key={`c-${activity.id}-${c.id}`}
                      className="whitespace-nowrap rounded-full bg-blue-100 !min-h-0 !min-w-0 px-3 py-1 text-xs text-black transition hover:bg-blue-300"
                    >
                      {c.name}
                    </span>
                  ) : (
                    <Link
                      key={`c-${activity.id}-${c.id}`}
                      to={`/circles/${c.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="whitespace-nowrap rounded-full bg-blue-100 !min-h-0 !min-w-0 px-3 py-1 text-xs text-black transition hover:bg-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-700"
                    >
                      {c.name}
                    </Link>
                  )
                )}
              </div>
            </div>
          )}

          {Array.isArray(activity.tags) && activity.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {activity.tags.map((tag, i) => (
                <button
                  key={`${activity.id}-tag-${i}-${tag}`}
                  onClick={() => {
                    if (!readOnly) onTagClick?.(tag);
                  }}
                  type="button"
                  className="text-blue-500 gap-1 text-sm rounded-md hover:text-blue-600 hover:font-semibold overflow-hidden leading-none !min-h-0 !min-h-0 !py-1"
                  style={{ WebkitAppearance: 'none' }}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}


        </div>

        {/* Media */}
        {(() => {
          const list = getMediaList(activity);
          if (!list.length) return null;

          const hasMultipleMedia = list.length > 1;
          const hasTags = Array.isArray(activity.tags) && activity.tags.length > 0;

          return (
            <div className={hasTags ? "mt-2" : "!mt-3"}>
              <div
                className={[
                  "flex gap-3 overflow-x-auto snap-x snap-mandatory",
                  hasMultipleMedia
                    ? "rounded-scroll"
                    : "no-scrollbar",
                ].join(" ")}
              >
                {list.map((m, idx) => {
                  const src = toAbsolute(m.url);
                  const isVid = isVideo(src) || isVideoMime(m.type);

                  // Multiple media must all match heights.
                  // Demo media should also always fill the available height.
                  const shouldFillHeight = hasMultipleMedia || readOnly;

                  const mediaClassName = shouldFillHeight
                  ? "block w-full h-[320px] sm:h-[390px] object-cover"
                  : "block w-full max-h-[320px] sm:max-h-[390px] object-contain";

                  return (
                    <div
                      key={`${activity.id}-media-${idx}`}
                      className={[
                        "relative min-w-full snap-start rounded-[22px] overflow-hidden bg-transparent",
                        shouldFillHeight ? "h-[320px] sm:h-[390px]" : "",
                      ].join(" ")}
                    >
                      {isVid ? (
                        <video
                          controls
                          playsInline
                          className={mediaClassName}
                          src={src}
                        />
                      ) : (
                        <img
                          src={src}
                          alt={`activity media ${idx + 1}`}
                          className={mediaClassName}
                          loading="lazy"
                          decoding="async"
                        />
                      )}

                      {hasMultipleMedia && (
                        <div className="absolute bottom-3 right-3 rounded-full bg-black/40 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                          {idx + 1}/{list.length}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Engagement row — below media and above comments */}
        <div className="ml-1 !mt-3 flex items-center justify-between gap-3 sm:!mt-4">
          {hfCount > 0 && (
            <button
              type="button"
              onClick={() => {
                if (!readOnly) setShowModal(true);
              }}
              disabled={readOnly}
              title="View engagement"
              className="flex items-center gap-1 shrink !min-h-0 !min-w-0"
            >
              <HandPalm
                size={14}
                weight="duotone"
                className="text-blue-600 shrink-0"
              />

              <span className="text-sm font-medium text-gray-700 shrink-0">
                {hfCount}
              </span>

              <div className="flex ml-2 -space-x-2 shrink-0">
                {avatarUsers.slice(0, 4).map((u) => (
                  <img
                    key={u.id}
                    src={toAvatar(u)}
                    alt={u.name || "user"}
                    className="w-7 h-7 rounded-full ring-2 ring-white object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ))}
              </div>
            </button>
          )}

          <button
            type="button"
            onClick={toggleHighFive}
            disabled={hfPending || readOnly}
            className={[
              "ml-auto shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full transition disabled:opacity-60 overflow-hidden leading-none !min-h-0 !min-w-0 px-3 py-1.5",
              hasHf
                ? "bg-blue-100 border-blue-100"
                : "bg-gray-100 border-gray-100 hover:bg-gray-50",
            ].join(" ")}
            style={{ lineHeight: 1 }}
            aria-pressed={hasHf}
            title={hasHf ? "Remove high five" : "High five!"}
          >
            <HandPalm
              size={14}
              weight={hasHf ? "duotone" : "regular"}
              className="text-blue-600 shrink-0"
            />
            <span className="text-xs sm:text-sm font-medium text-gray-800 select-none whitespace-nowrap">
              {hasHf ? "High fived!" : "High five!"}
            </span>
          </button>
        </div>

        {(!readOnly || activity.demo_comments?.length > 0) && (
          <CommentsSection
            activityId={activity.id}
            currentUser={currentUser}
            className="pt-2"
            readOnly={readOnly}
            initialComments={activity.demo_comments || []}
            onCountChange={(upd) => {
              if (typeof upd === "function") {
                setCommentCount(upd);
              } else if (typeof upd === "number") {
                setCommentCount(upd);
              }
            }}
          />
        )}

        {!readOnly && (
          <EngagementModal
            activityId={activity.id}
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            currentUser={currentUser}
            defaultTab="highfives"
          />
        )}
      </div>
    );
  }

  /** ---------------- EDIT MODE (uses ActivityFormFields verbatim) ---------------- */
  return (
    <div className={`bg-white rounded-2xl ${contentClassName} space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-blue-600 font-semibold">Edit Activity</h3>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <ActivityFormFields
        form={form}
        setForm={setForm}
        circleId={null}
        joinedCircles={joinedCircles}
        showCircles={true}
        showTimestamp={true}
        previewUrl={previewUrl}
        setPreviewUrl={setPreviewUrl}
        currentMediaUrls={(form.existing_media_urls || []).map(toAbsolute)}
      />

      <div className="flex justify-end items-center gap-2">
        <button
          type="button"
          onClick={cancelEdit}
          disabled={saving}
          className="px-3 !min-h-0 !h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-sm"
        >
          Cancel
        </button>

        {/* Delete button with confirmation flow */}
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          disabled={saving}
          className="px-3 !min-h-0 !h-8 rounded-full bg-red-400/80 text-white hover:bg-red-400 text-sm"
        >
          Delete
        </button>

        <button
          type="button"
          onClick={saveEdit}
          disabled={saving}
          className="px-3 !min-h-0 !h-8 rounded-full bg-blue-700 text-white hover:bg-blue-800 text-sm"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Confirmation modal */}
      {confirmingDelete &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => {
                if (!deleting) setConfirmingDelete(false);
              }}
              aria-hidden="true"
            />

            <div className="relative z-10 w-full max-w-sm md:max-w-[360px] rounded-full bg-white py-6 shadow-[0_8px_0px_rgba(37,99,235,0.96)]">
              <div className="flex items-start justify-center gap-4">
                <div>
                  <p className="text-md font-medium leading-tight text-black">
                    Delete this activity?
                  </p>
                  <p className="text-sm text-black">
                    This can't be undone
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className="rounded-full border border-blue-700 px-3 py-1.5 text-sm disabled:opacity-50 hover:ring-2 hover:ring-blue-700"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={deleteActivity}
                  disabled={deleting}
                  className="rounded-full bg-red-600 px-3 py-2 text-sm text-white hover:ring-2 hover:ring-red-300 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ActivityCard;
